import SearchIcon from '@mui/icons-material/Search';
import FormControl from '@mui/material/FormControl';
import debounce from 'debounce-promise';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { components, IndicatorProps } from 'react-select';
import AsyncSelect from 'react-select/async';
import { usePolicyholder } from '../../api/PolicyholderApi';
import SearchApi from '../../api/SearchApi';
import { AuthContext, AuthData } from '../../context/auth';
import { ConfigContext, ConfigData } from '../../context/config';
import { socotraBlue2Faded } from '../../data/globalStylingVariables';
import { PolicyholderResponse } from '../../types/policyholder';
import FieldLabel from '../fields/FieldLabel';
import SearchCard from '../search/SearchCard';
import { getTemplateResult } from '../search/util';
import { errorToast } from '../Toast';

interface Props {
	authData: AuthData;
	configData: ConfigData;
	update?: (val: string) => void;
}

const PolicyholderSearch = (props: Props) => {
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const policyholderId = searchParams.get('policyholderId') || null;
	const [policyholderValue, setPolicyholderValue] =
		useState<{ value: string; label: string }>();
	const { configData, authData, update } = props;
	const { auth } = authData;
	const { authorizationToken } = auth;

	const { data: policyholderData, isError } = usePolicyholder(
		policyholderId,
		authorizationToken,
	);

	const getPolicyholderData = (searchResult: any, configData: ConfigData) => {
		const policyholderCardDefinition =
			configData.policyholderCardConfig.workflows.find(
				(w: any) => w.message === 'policyHolderSearch.json',
			).value;
		const title1 = getTemplateResult(
			policyholderCardDefinition.title,
			configData.ontologyData.items,
			searchResult.entity.values,
		);
		const title2 = getTemplateResult(
			policyholderCardDefinition.subtitle,
			configData.ontologyData.items,
			searchResult.entity.values,
		);
		const subtitle1 = getTemplateResult(
			policyholderCardDefinition.tag,
			configData.ontologyData.items,
			searchResult.entity.values,
		);
		const value = `/policyholder/${searchResult.entity.id}`;
		const policyholderLabel = [title2, subtitle1].filter((v) => !!v).join(' ');
		const label = `${title1} - ${policyholderLabel}`;

		return { value, label, title1, title2, subtitle1 };
	};

	useEffect(() => {
		const getDefaultPolicyholder = (searchResult: PolicyholderResponse) => {
			if (!searchResult.entity) {
				return '';
			}
			return getPolicyholderData(searchResult, configData).label;
		};

		if (policyholderData?.data) {
			const policyholderValue = getDefaultPolicyholder(policyholderData.data);
			const initialPolicyholder = {
				value: policyholderValue,
				label: policyholderValue,
			};
			setPolicyholderValue(initialPolicyholder);
			update(policyholderData.data.locator);
		}
	}, [configData, policyholderData, setPolicyholderValue, update]);

	if (isError) {
		setPolicyholderValue(null);
	}

	const getSearchResultData = (searchResult: any) => {
		return getPolicyholderData(searchResult, configData);
	};

	const loadSearchResults = (inputValue: string) => {
		return new Promise(async (resolve) => {
			try {
				const searchResults = await SearchApi.search(
					authorizationToken,
					inputValue,
				);
				const searchData = searchResults.data.results;
				const policyholderResults =
					searchData &&
					searchData.filter((r) => r.entityType === 'entity.policyholder');

				resolve(policyholderResults);
			} catch {
				errorToast('Failed to run search query');
				resolve([]);
			}
		});
	};

	const DropdownIndicator = (dropdownProps: IndicatorProps<{}, false>) => {
		return (
			<components.DropdownIndicator {...dropdownProps}>
				<SearchIcon />
			</components.DropdownIndicator>
		);
	};

	const loadOptions = (inputValue: string) => {
		return new Promise(async (resolve: (value: any[]) => void) => {
			try {
				const searchResults: any = await loadSearchResults(inputValue);
				const options = searchResults
					.map((result: any) => {
						return getSearchResultData(result);
					})
					.filter((r: any) => !!r);

				resolve(options);
			} catch (error) {
				console.error(error);
				resolve([]);
			}
		});
	};

	const debouncedLoadOptions = debounce(loadOptions, 1000);

	const onChange = (value: any) => {
		const policyholderLocator = value.value.split('/').pop();
		setPolicyholderValue(value);
		update(policyholderLocator);
		searchParams.set('policyholderId', policyholderLocator);
		navigate(`/startNewApplication?${searchParams.toString()}`);
	};

	const customStyles = {
		container: (provided: any) => ({
			...provided,
			width: '100%',
			marginTop: '5px',
		}),
		control: (provided: any) => ({
			...provided,
			borderColor: 'rgba(0, 0, 0, 0.42)',
			boxShadow: null,
			backgroundColor: '',
			minHeight: '56px',
		}),
		option: (provided: any, state: any) => ({
			...provided,
			backgroundColor: state.isSelected
				? socotraBlue2Faded
				: state.isFocused
				? socotraBlue2Faded
				: 'transparent',
			color: '#000000',
			'&:hover': {
				backgroundColor: socotraBlue2Faded,
			},
		}),
	};

	const CustomOption = (customOptionProps: any) => {
		return (
			<components.Option {...customOptionProps}>
				<SearchCard {...customOptionProps.data} />
			</components.Option>
		);
	};

	return (
		<FormControl style={{ width: '100%' }}>
			<FieldLabel title={'Select Policyholder'} required />
			<AsyncSelect
				styles={customStyles}
				value={policyholderValue}
				loadOptions={(inputValue: string) => debouncedLoadOptions(inputValue)}
				onChange={onChange}
				placeholder='Search Policyholders'
				components={{ DropdownIndicator, Option: CustomOption }}
				noOptionsMessage={() => 'No Results Found'}
			/>
		</FormControl>
	);
};

function withContext(Component: any) {
	return (
		props: Omit<Props, 'authData' | 'configData' | 'policyholderData'>,
	) => (
		<ConfigContext.Consumer>
			{(configData) => (
				<AuthContext.Consumer>
					{(authData) => (
						<Component {...props} authData={authData} configData={configData} />
					)}
				</AuthContext.Consumer>
			)}
		</ConfigContext.Consumer>
	);
}

export default withContext(PolicyholderSearch);
